/**
 * VISTOERA_PLATFORM_ADMIN_MARKETING_ANALYTICS_HOME_AND_TRACKING_FOUNDATION_V1
 * — the client-side half of the tracking foundation (task spec §12/§16-26/
 * §96-104).
 *
 * Deliberately tiny: one fire-and-forget POST per page load to
 * `/api/public/marketing/track`, echoing back the URL/referrer for the
 * backend to derive UTM/click-id/source/medium/country/device from —
 * never computed here (task spec: the server, not the client, is the one
 * source of truth for attribution classification). No canvas/font/audio/
 * hardware fingerprinting anywhere in this module (task spec §37).
 *
 * PERFORMANCE (task spec §96) — uses `fetch(..., { keepalive: true })`
 * without ever being `await`ed by a caller, and is scheduled after the
 * page's own first paint via `requestIdleCallback`/`setTimeout` fallback,
 * so this can never delay LCP/navigation. FAIL OPEN (task spec §97) — a
 * bare `.catch(() => {})`, no retry storm, never surfaces to the user.
 *
 * SCOPE (task spec §75/§76) — one beacon per real page load. This V1
 * does not additionally re-fire on every client-side SPA route change
 * (a documented, intentional simplification — see
 * `docs/analytics/MARKETING_ANALYTICS_ARCHITECTURE.md` §Tracking SDK
 * Scope); first-touch attribution (the P0 requirement, task spec §20)
 * is fully captured by this single call since it always fires on the
 * very first page of a new visitor's journey.
 *
 * CONSENT (task spec §53/§56) — this first-party call records only
 * acquisition/attribution data Vistoera itself needs for signup
 * attribution (task spec §54's "Essential" category — no third-party ad
 * script is loaded here, nothing is shared with Google/Meta/etc. by this
 * module). A future Consent Manager can gate this call behind an
 * `ESSENTIAL` category check without changing this module's contract —
 * see `CONSENT_UI_FOLLOWUP_REQUIRED` in the final report.
 */

import {
  MARKETING_SESSION_ID_STORAGE_KEY,
  MARKETING_VISITOR_ID_STORAGE_KEY,
  readMarketingStorageValue,
  writeMarketingStorageValue,
} from './marketingVisitorStorage'

// Same env-var-derivation contract as `saas/api.ts`'s `SAAS_API_BASE_URL`
// (VITE_SAAS_API_BASE_URL, else derived from VITE_API_BASE_URL, else a
// page-relative `/api/...` — see that module's "Phase S1 incident
// (2026-08-10)" comment for why the fallback chain matters on a static
// host). Deliberately re-derived here rather than imported from
// `saas/api.ts` to keep this module free of any dependency on that much
// larger module (avoids a needless load-order coupling for a page that
// may render before `saas/api.ts` is otherwise needed, e.g. the public
// marketing site).
function resolveTrackingEndpoint(): string {
  const rawSaasBase = (import.meta.env.VITE_SAAS_API_BASE_URL as string | undefined)?.trim()
  const rawApiBase = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.trim()
  const fromSaasBase = rawSaasBase?.replace(/\/+$/, '').replace(/\/api\/saas$/, '')
  const fromApiBase = rawApiBase?.replace(/\/+$/, '').replace(/\/api$/, '')
  const origin = fromSaasBase || fromApiBase || ''
  return `${origin}/api/public/marketing/track`
}

let didInit = false

function sendBeacon(): void {
  try {
    const body = JSON.stringify({
      visitor_id: readMarketingStorageValue(MARKETING_VISITOR_ID_STORAGE_KEY) || undefined,
      session_id: readMarketingStorageValue(MARKETING_SESSION_ID_STORAGE_KEY) || undefined,
      url: window.location.href,
      referrer: document.referrer || undefined,
    })

    fetch(resolveTrackingEndpoint(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
      credentials: 'omit',
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data: { visitor_id?: string; session_id?: string; recorded?: boolean } | null) => {
        if (data?.visitor_id) writeMarketingStorageValue(MARKETING_VISITOR_ID_STORAGE_KEY, data.visitor_id)
        if (data?.session_id) writeMarketingStorageValue(MARKETING_SESSION_ID_STORAGE_KEY, data.session_id)
      })
      .catch(() => {
        // Fail open (task spec §96/§97) — never surfaced, never retried.
      })
  } catch {
    // Fail open — a synchronous throw here (e.g. `JSON.stringify` on a
    // hostile `document.referrer`) must never propagate into the app.
  }
}

/**
 * Idempotent, safe to call from every Surface's root component. Schedules
 * the one beacon this V1 sends per page load off the main paint path.
 */
export function initMarketingTracking(): void {
  if (didInit) return
  didInit = true
  if (typeof window === 'undefined') return

  const schedule = (window as unknown as { requestIdleCallback?: (cb: () => void) => number }).requestIdleCallback
  if (typeof schedule === 'function') {
    schedule(sendBeacon)
  } else {
    window.setTimeout(sendBeacon, 0)
  }
}
